import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Save, ArrowLeft, MapPin } from 'lucide-react';
import { useObrasFixas } from '../../context/ObrasFixasContextSupabase';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// Configurar ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-shadow.png',
});

// Componente para selecionar localização no mapa
const LocationMarker = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return position ? <Marker position={position} /> : null;
};

const CadastroObraFixa = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { adicionarObraFixa, atualizarObraFixa, obterObraFixaPorId } = useObrasFixas();
    const [mostrarMapa, setMostrarMapa] = useState(false);
    const [position, setPosition] = useState(null);
    const [nomeLocal, setNomeLocal] = useState('');
    const [descricaoLocal, setDescricaoLocal] = useState('');

    // Coordenadas de Conceição do Castelo - ES
    const centroConceicaoCastelo = [-20.359292, -41.247282];

    const { register, handleSubmit, setValue, formState: { errors } } = useForm({
        defaultValues: {
            nome: '',
            empreiteiro: '',
            valor: '',
            status: 'Planejada',
            dataInicio: '',
            dataPrevisaoTermino: '',
            observacoes: '',
            contrato: '',
            licitacao: '',
            processo: '',
            fiscaisObras: '',
            gestor: '',
            totalMedicoes: 0
        }
    });

    useEffect(() => {
        if (id) {
            const obraFixa = obterObraFixaPorId(id);
            if (obraFixa) {
                Object.keys(obraFixa).forEach(key => {
                    if (key !== 'localizacao' && key !== 'itens') {
                        setValue(key, obraFixa[key]);
                    }
                });
                
                if (obraFixa.localizacao) {
                    if (obraFixa.localizacao.latitude && obraFixa.localizacao.longitude) {
                        setPosition([obraFixa.localizacao.latitude, obraFixa.localizacao.longitude]);
                    }
                    setNomeLocal(obraFixa.localizacao.nome || '');
                    setDescricaoLocal(obraFixa.localizacao.descricao || '');
                }
            }
        }
    }, [id, obterObraFixaPorId, setValue]);

    const onSubmit = async (data) => {
        try {
            const dadosCompletos = {
                ...data,
                localizacao: position ? {
                    latitude: position[0],
                    longitude: position[1],
                    nome: nomeLocal,
                    descricao: descricaoLocal
                } : null
            };

            if (id) {
                await atualizarObraFixa(id, dadosCompletos);
            } else {
                await adicionarObraFixa(dadosCompletos);
            }
            navigate('/obras-fixas');
        } catch (error) {
            console.error('Erro ao salvar obra fixa:', error);
        }
    };

    const abrirSeletorLocalizacao = () => {
        setMostrarMapa(true);
    };

    const fecharSeletorLocalizacao = () => {
        setMostrarMapa(false);
    };
    
    // Formatar valor monetário
    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    // Formatar coordenadas para exibição
    const formatarCoordenadas = (latitude, longitude) => {
        if (!latitude || !longitude) return 'Não definido';
        return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    };

    return (
        <div>
            <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                    <button
                        type="button"
                        className="button secondary"
                        onClick={() => navigate('/obras-fixas')}
                    >
                        <ArrowLeft size={18} />
                        Voltar
                    </button>
                    <h2>{id ? 'Editar Obra Fixa' : 'Nova Obra Fixa'}</h2>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="form-group">
                        <label>Nome da Obra Fixa *</label>
                        <input
                            type="text"
                            {...register('nome', { required: 'Nome da obra fixa é obrigatório' })}
                        />
                        {errors.nome && <span style={{ color: 'red', fontSize: '14px' }}>{errors.nome.message}</span>}
                    </div>

                    <div className="form-row-4">
                        <div className="form-group">
                            <label>Empreiteiro Responsável *</label>
                            <input
                                type="text"
                                {...register('empreiteiro', { required: 'Empreiteiro é obrigatório' })}
                            />
                            {errors.empreiteiro && <span style={{ color: 'red', fontSize: '14px' }}>{errors.empreiteiro.message}</span>}
                        </div>

                        <div className="form-group">
                            <label>Valor da Obra (R$) *</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                {...register('valor', {
                                    required: 'Valor é obrigatório',
                                    min: { value: 0, message: 'Valor deve ser positivo' }
                                })}
                            />
                            {errors.valor && <span style={{ color: 'red', fontSize: '14px' }}>{errors.valor.message}</span>}
                        </div>

                        <div className="form-group">
                            <label>Status *</label>
                            <select {...register('status', { required: 'Status é obrigatório' })}>
                                <option value="Planejada">Planejada</option>
                                <option value="Em execução">Em execução</option>
                                <option value="Finalizada">Finalizada</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Data de Início *</label>
                            <input
                                type="date"
                                {...register('dataInicio', { required: 'Data de início é obrigatória' })}
                            />
                            {errors.dataInicio && <span style={{ color: 'red', fontSize: '14px' }}>{errors.dataInicio.message}</span>}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Previsão de Término *</label>
                            <input
                                type="date"
                                {...register('dataPrevisaoTermino', { required: 'Data de previsão é obrigatória' })}
                            />
                            {errors.dataPrevisaoTermino && <span style={{ color: 'red', fontSize: '14px' }}>{errors.dataPrevisaoTermino.message}</span>}
                        </div>
                        
                        <div className="form-group">
                            <label>Observações</label>
                            <textarea
                                {...register('observacoes')}
                                placeholder="Informações adicionais sobre a obra..."
                            />
                        </div>
                    </div>

                    {/* Novos campos adicionados */}
                    <div className="form-row-3">
                        <div className="form-group">
                            <label>Número do Contrato</label>
                            <input
                                type="text"
                                {...register('contrato')}
                                placeholder="Ex: CT-2024-001"
                            />
                        </div>

                        <div className="form-group">
                            <label>Número da Licitação</label>
                            <input
                                type="text"
                                {...register('licitacao')}
                                placeholder="Ex: PP-2024-001"
                            />
                        </div>

                        <div className="form-group">
                            <label>Número do Processo</label>
                            <input
                                type="text"
                                {...register('processo')}
                                placeholder="Ex: PROC-2024-001"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Gestor da Obra</label>
                            <input
                                type="text"
                                {...register('gestor')}
                                placeholder="Nome do gestor responsável"
                            />
                        </div>

                        <div className="form-group">
                            <label>Fiscais de Obras</label>
                            <textarea
                                {...register('fiscaisObras')}
                                placeholder="Lista dos fiscais responsáveis (separados por vírgula)"
                                rows="3"
                            />
                        </div>
                    </div>

                    {/* Campo calculado - Total de Medições */}
                    <div className="form-group">
                        <label>Total de Medições</label>
                        <div className="medida-display" style={{ 
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            color: '#495057'
                        }}>
                            {id && obterObraFixaPorId(id) ? (
                                <>
                                    {parseFloat(obterObraFixaPorId(id).totalMedicoes || 0).toFixed(2)}
                                    <span style={{ marginLeft: '5px', color: '#666' }}>
                                        {obterObraFixaPorId(id)?.itens && obterObraFixaPorId(id).itens.length > 0 && obterObraFixaPorId(id).itens[0].unidade ? 
                                            obterObraFixaPorId(id).itens[0].unidade : 
                                            'un'
                                        }
                                    </span>
                                </>
                            ) : (
                                <>
                                    0.00
                                    <span style={{ marginLeft: '5px', color: '#666' }}>un</span>
                                </>
                            )}
                        </div>
                        <input
                            type="hidden"
                            {...register('totalMedicoes')}
                        />
                        <small style={{ color: '#666', fontSize: '12px' }}>
                            Este valor é calculado automaticamente com base na soma de todas as quantidades medidas nos itens da obra.
                        </small>
                    </div>

                    {/* Localização da Obra Fixa */}
                    <div className="form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <label>Localização da Obra Fixa *</label>
                            <button
                                type="button"
                                className="button"
                                onClick={abrirSeletorLocalizacao}
                            >
                                <MapPin size={16} />
                                Selecionar no Mapa
                            </button>
                        </div>

                        {!position ? (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                border: '2px dashed #dee2e6'
                            }}>
                                <p style={{ margin: 0, color: '#666' }}>
                                    Nenhuma localização selecionada. Clique em "Selecionar no Mapa" para definir o ponto da obra.
                                </p>
                            </div>
                        ) : (
                            <div style={{ 
                                padding: '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                border: '1px solid #dee2e6'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <h4 style={{ margin: '0 0 5px 0', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <MapPin size={16} style={{ color: '#0d6efd' }} />
                                            Coordenadas: {formatarCoordenadas(position[0], position[1])}
                                        </h4>
                                        
                                        <div style={{ marginTop: '15px' }}>
                                            <div className="form-group" style={{ marginBottom: '10px' }}>
                                                <label>Nome do Local</label>
                                                <input
                                                    type="text"
                                                    value={nomeLocal}
                                                    onChange={(e) => setNomeLocal(e.target.value)}
                                                    placeholder="Ex: Praça Central, Escola Municipal, etc."
                                                />
                                            </div>
                                            
                                            <div className="form-group">
                                                <label>Descrição do Local</label>
                                                <textarea
                                                    value={descricaoLocal}
                                                    onChange={(e) => setDescricaoLocal(e.target.value)}
                                                    placeholder="Detalhes adicionais sobre a localização..."
                                                    rows="2"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!position && (
                            <span style={{ color: 'red', fontSize: '14px', marginTop: '8px', display: 'block' }}>
                                É necessário selecionar a localização da obra fixa
                            </span>
                        )}
                    </div>

                    {/* Nota sobre medições */}
                    <div className="form-group" style={{ marginTop: '30px' }}>
                        <div style={{ 
                            padding: '15px', 
                            backgroundColor: '#f0f8ff', 
                            borderRadius: '8px',
                            border: '1px solid #b8daff',
                            marginBottom: '20px'
                        }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#004085', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>Medições e Itens</span>
                            </h4>
                            <p style={{ margin: '0', color: '#004085' }}>
                                Após cadastrar a obra fixa, você poderá gerenciar os itens e medições através do botão "Medições" na lista de obras fixas.
                            </p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '30px' }}>
                        <button
                            type="button"
                            className="button secondary"
                            onClick={() => navigate('/obras-fixas')}
                        >
                            Cancelar
                        </button>
                        <button 
                            type="submit" 
                            className="button"
                            disabled={!position}
                        >
                            <Save size={18} />
                            {id ? 'Atualizar Obra Fixa' : 'Cadastrar Obra Fixa'}
                        </button>
                    </div>
                </form>
            </div>

            {mostrarMapa && (
                <div className="modal-overlay">
                    <div className="modal" style={{ 
                        width: '80%', 
                        height: '80%', 
                        maxWidth: '1200px',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        <div className="modal-header">
                            <h3 className="modal-title">Selecionar Localização da Obra Fixa</h3>
                            <button className="close-button" onClick={fecharSeletorLocalizacao}>×</button>
                        </div>
                        <div style={{ 
                            flex: 1,
                            padding: '0',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            minHeight: 0
                        }}>
                            <div style={{ 
                                backgroundColor: '#f8f9fa', 
                                padding: '10px', 
                                borderRadius: '4px',
                                marginBottom: '10px',
                                fontSize: '14px',
                                color: '#666',
                                flexShrink: 0
                            }}>
                                Clique no mapa para selecionar a localização exata da obra fixa.
                            </div>
                            
                            <div style={{ 
                                flex: 1,
                                width: '100%',
                                minHeight: 0
                            }}>
                                <MapContainer
                                    center={position || centroConceicaoCastelo}
                                    zoom={13}
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    />
                                    <LocationMarker position={position} setPosition={setPosition} />
                                </MapContainer>
                            </div>
                            
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                padding: '15px',
                                backgroundColor: '#f8f9fa',
                                borderTop: '1px solid #dee2e6',
                                flexShrink: 0
                            }}>
                                <div>
                                    {position ? (
                                        <span>
                                            <strong>Coordenadas selecionadas:</strong> {formatarCoordenadas(position[0], position[1])}
                                        </span>
                                    ) : (
                                        <span>Nenhuma localização selecionada</span>
                                    )}
                                </div>
                                <div>
                                    <button 
                                        className="button secondary" 
                                        onClick={() => setPosition(null)}
                                        style={{ marginRight: '10px' }}
                                    >
                                        Limpar
                                    </button>
                                    <button 
                                        className="button" 
                                        onClick={fecharSeletorLocalizacao}
                                        disabled={!position}
                                    >
                                        Confirmar Localização
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CadastroObraFixa;